import {
	useCallback, useEffect, useRef,
} from 'react';
import API from 'Vulture/lib/API.js';
import {
	createBackfillIndicator,
} from '../../utils/vclUtils';

// Static provider types for known/legacy providers
const knownProviderTypes = {
	'Slide Deck':     'slide_deck',
	'Published Feed': 'published_feed',
	'Infotainment':   'infotainment',
	'Vistar':         'vistar',
};

// Published feeds are cross-account slide decks — they render, slot, and
// backfill exactly like decks; only their fetch path differs.
const isDeckLikeType = (type) => {
	return type === 'slide_deck' || type === 'published_feed';
};

// Helper function to get or create provider type mapping
const getProviderType = (providerData) => {
	// If providerData has a type field, use it directly
	if (providerData.type) {
		return providerData.type;
	}

	// For legacy providers, try to find in known types
	if (providerData.name && knownProviderTypes[providerData.name]) {
		return knownProviderTypes[providerData.name];
	}

	// Default to slide_deck for backward compatibility
	return 'slide_deck';
};

// Helper function to generate display name from provider type
const getProviderDisplayName = (provider) => {
	// If provider has a name, use it
	if (provider.name && provider.name !== 'Untitled Provider') {
		return provider.name;
	}

	// For providers without names, generate from type
	const type = getProviderType(provider);

	if (type === 'slide_deck') {
		return provider.name || 'Untitled Deck';
	}

	// Convert type to readable name: "nova" -> "Nova", "sports_central" -> "Sports Central"
	return type
		.split('_')
		.map((word) => {
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join(' ');
};

/**
 * VCL-specific provider data management hook
 * Handles provider operations specific to Visual Content Loop functionality
 */
export const useVCLProviderData = (visualContentLoopId, transformSlide, transformApiSlides, dynamicDurations, accountUUID) => {
	const abortControllerRef = useRef(new AbortController());

	// Slide-deck responses cached for the lifetime of one open VCL, so changing the Loop Date
	// re-filters the same payloads instead of refetching every deck.
	//
	// Nothing notifies this cache when a deck is edited elsewhere in the app, so it is
	// dropped at the points where a refetch is cheap and staleness is most likely: Refresh
	// (which bypasses it via useFreshData), switching VCL (here), and Save (below).
	const slideDeckCacheRef = useRef({});

	const clearSlideDeckCache = useCallback(() => {
		slideDeckCacheRef.current = {};
	}, []);

	useEffect(() => {
		clearSlideDeckCache();
	}, [ visualContentLoopId, clearSlideDeckCache, ]);

	// Function to fetch fresh VCL data from API.
	// Rejects rather than falling back to []: the caller installs the result as the loop AND
	// as the save baseline, so a swallowed failure would read as "this loop is empty" and the
	// next Save would PUT that empty list over the loop's real providers.
	const fetchFreshVCLData = useCallback(async () => {
		const response = await API.findRecord(
			'visual_content_loop',
			visualContentLoopId,
			{},
			abortControllerRef.current.signal
		);

		if (!response) {
			throw new Error('Visual content loop could not be read.');
		}

		// A record with no providers is a genuinely empty loop, which is a real answer.
		return response.providers || [];
	}, [ visualContentLoopId, ]);

	// Initialize providers from props and fetch slide data for slide decks
	const getInitialProviders = useCallback(async (sourceProviders, useFreshData = false, signal = null) => {
		// Use providers from props or fetch fresh data from API
		let providersToProcess;

		if (useFreshData) {
			providersToProcess = await fetchFreshVCLData();
		} else {
			providersToProcess = sourceProviders || [];
		}

		const campaigns = [];
		const backfillContent = [];

		// Process each provider and fetch slide data for slide decks
		for (const provider of providersToProcess) {
			const providerType = getProviderType(provider);
			let slides = [];
			let providerName = getProviderDisplayName(provider); // Use helper to generate proper names
			let unavailable = false;
			let suppressed = false;

			// If this is a slide deck (or published feed) provider, fetch the
			// current slide data. Published feeds resolve through the
			// feed-authorized route — the raw slide_deck route is account-scoped
			// and would reject the cross-account read.
			// Feed hydration needs the loop's account UUID in the route path —
			// without it, skip the fetch and leave the provider's slides empty.
			const canHydrate = providerType !== 'published_feed' || Boolean(accountUUID);
			if (isDeckLikeType(providerType) && canHydrate) {
				try {
					let slideDeck;
					// Cache key is type-scoped so a deck and a feed sharing an id never collide.
					const cacheKey = `${providerType}:${provider.id}`;

					if (!useFreshData && slideDeckCacheRef.current[cacheKey]) {
						slideDeck = slideDeckCacheRef.current[cacheKey];
					} else {
						let response;
						if (providerType === 'published_feed') {
							response = await API.getPublishedFeedDeck(provider.id, accountUUID, signal || abortControllerRef.current.signal);
						} else {
							response = await API.findRecord('slide_deck', provider.id, {
								includes: [
									'slides.folder.files',
									'slides.galleryFile',
									'slides.slide',
									'slides.folderSchedules',
								],
							}, signal || abortControllerRef.current.signal);
						}

						if (response) {
							slideDeck = response;
							slideDeckCacheRef.current[cacheKey] = slideDeck;
						}
					}

					if (slideDeck) {
						// Use the fresh name from the slide deck API
						providerName = slideDeck.name || 'Untitled Deck';

						slideDeck.slides.sort((a, b) => {
							return (a.order || 0) - (b.order || 0);
						});

						// Transform the nested slides structure from API
						// Published feeds: apply schedules with the publisher's flag, not ours
						const deckFlagOverride = providerType === 'published_feed'
							? slideDeck.account?.is_in_folder_scheduling_enabled
							: undefined;
						const transformedSlides = transformApiSlides(slideDeck.slides || [], true, deckFlagOverride);

						// Slice by slot count, not item count (folders can take multiple slots)
						const maxSlots = provider.slots ?? 1;
						let currentSlots = 0;
						slides = [];
						for (const slide of transformedSlides) {
							slide.folderSlots = slide.folderSlots ?? 1;
							const isFolder = slide.itemType === 'folder';
							const slideSlots = isFolder ? slide.folderSlots : 1;
							if (currentSlots + slideSlots <= maxSlots) {
								slides.push(slide);
								currentSlots += slideSlots;
							} else if (isFolder && slideSlots > 0 && maxSlots > currentSlots) {
								slide.folderSlots = maxSlots - currentSlots;
								slides.push(slide);
								currentSlots += slide.folderSlots;
								break;
							} else {
								break;
							}
						}
					}
				} catch (error) {
					// If fetch fails, continue with empty slides array
					if (error.name !== 'AbortError' && providerType === 'published_feed') {
						const status = error.status || (error.message && error.message.includes('HTTP error: 410') && 410) || (error.message && error.message.includes('HTTP error: 404') && 404);
						if (status === 410) {
							// 410 Gone = feed truly unpublished (permanent) —
							// grayed tombstone; saveProviders drops it on the next save.
							unavailable = true;
							providerName = 'Unpublished Feed';
						} else if (status === 404) {
							// 404 = reversibly suppressed (source flag off, publisher
							// membership/user removed, deck deleted) — gray it but KEEP
							// the reference so restoring the source revives the entry.
							suppressed = true;
							providerName = 'Feed Unavailable';
						}
					}
					slides = [];
				}
			}

			const isBackfill = Boolean(provider.isBackfill ?? provider.is_backfill ?? false);

			let childrenWithBackfill = [];

			// Only add backfill indicator for slide deck / published feed providers
			// Dynamic providers (nova, sports_central, etc.) manage their own content
			if (isDeckLikeType(providerType)) {
				// Transform slides and add backfill indicator if needed
				const transformedSlides = slides.map(transformSlide).filter(Boolean);
				childrenWithBackfill = [ ...transformedSlides, ];

				if (!isBackfill && !unavailable && !suppressed) {
					const availableSlots = provider.slots ?? 1;
					// Calculate used slots, accounting for folders that take multiple slots
					const usedSlots = transformedSlides.reduce((total, slide) => {
						// Folders take up slide.slots slots (0 for empty folders), regular slides take 1 slot
						return total + (slide.itemType === 'folder' ? (slide.slots ?? 1) : 1);
					}, 0);
					const backfillSlots = Math.max(0, availableSlots - usedSlots);

					if (backfillSlots > 0) {
						childrenWithBackfill.push(createBackfillIndicator(provider.id, backfillSlots));
					}
				}
			}

			const transformedProvider = {
				id:         provider.id,
				name:       providerName, // Use the dynamically fetched name
				slots:      provider.slots ?? 1,
				order:      provider.order || 1,
				isBackfill: isBackfill,
				isDeleted:  provider.isDeleted || false,
				type:       providerType,
				category:   provider.category ?? null, // Preserve category from backend data
				expanded:   provider.expanded !== undefined ? provider.expanded : (slides.length > 0), // Expand by default if has slides
				children:   isDeckLikeType(providerType) ? childrenWithBackfill : ((provider.children || []).filter((c) => !c.isBackfillIndicator)),
				settings:   provider.settings || {},
				unavailable, // published_feed 410'd (truly unpublished — dropped on save)
				suppressed,  // published_feed 404'd (reversibly unavailable — kept)
			};

			// Apply dynamic durations for infotainment providers
			if (transformedProvider.category === 'infotainment' && dynamicDurations && dynamicDurations[transformedProvider.type]) {
				transformedProvider.duration = dynamicDurations[transformedProvider.type];
			} else if (transformedProvider.category === 'advertising') {
				transformedProvider.duration = 15; // Default for advertising
			}

			if (transformedProvider.isBackfill) {
				backfillContent.push(transformedProvider);
			} else {
				campaigns.push(transformedProvider);
			}
		}

		// Sort by order
		campaigns.sort((a, b) => {
			return a.order - b.order;
		});
		backfillContent.sort((a, b) => {
			return a.order - b.order;
		});

		return {
			campaigns,
			backfillContent,
		};
	}, [ fetchFreshVCLData, transformSlide, dynamicDurations, transformApiSlides, accountUUID, ]);

	// Function for adding new providers (updated to handle slide deck data)
	const addProviderToVCL = useCallback((providerData, addContentType, campaigns, backfillContent) => {
		console.log('Adding provider to VCL:', {
			providerData,
			addContentType,
		});

		const defaultSlots = 1;
		const slides = providerData.slides || [];
		const providerType = getProviderType(providerData);

		// Slice by slot count, not item count (folders can take multiple slots)
		const maxSlots = defaultSlots;
		let currentSlots = 0;
		const slicedSlides = [];
		for (const slide of slides) {
			slide.folderSlots = slide.folderSlots ?? 1;
			const isFolder = slide.itemType === 'folder';
			const slideSlots = isFolder ? slide.folderSlots : 1;
			if (currentSlots + slideSlots <= maxSlots) {
				slicedSlides.push(slide);
				currentSlots += slideSlots;
			} else if (isFolder && slideSlots > 0 && maxSlots > currentSlots) {
				slide.folderSlots = maxSlots - currentSlots;
				slicedSlides.push(slide);
				currentSlots += slide.folderSlots;
				break;
			} else {
				break;
			}
		}
		const transformedSlides = slicedSlides.map(transformSlide).filter(Boolean);

		// Only add backfill indicator for slide deck providers
		// Dynamic providers (nova, sports_central, etc.) manage their own content
		const childrenWithBackfill = [ ...transformedSlides, ];
		if (isDeckLikeType(providerType) && addContentType !== 'backfill') {
			const availableSlots = defaultSlots;
			// Calculate used slots, accounting for folders that take multiple slots
			const usedSlots = transformedSlides.reduce((total, slide) => {
				// Folders take up slide.slots slots (0 for empty folders), regular slides take 1 slot
				return total + (slide.itemType === 'folder' ? (slide.slots ?? 1) : 1);
			}, 0);
			const backfillSlots = Math.max(0, availableSlots - usedSlots);

			if (backfillSlots > 0) {
				childrenWithBackfill.push(createBackfillIndicator(providerData.id, backfillSlots));
			}
		}

		// Calculate order based on non-deleted items only
		const nonDeletedCampaigns = campaigns.filter((p) => {
			return !p.isDeleted;
		});
		const nonDeletedBackfill = backfillContent.filter((p) => {
			return !p.isDeleted;
		});

		const newProvider = {
			id:         providerData.id,
			name:       getProviderDisplayName(providerData),
			type:       getProviderType(providerData),
			category:   providerData.category || null, // Preserve category from modal
			slots:      defaultSlots,
			isBackfill: addContentType === 'backfill',
			order:      addContentType === 'content' ? nonDeletedCampaigns.length + 1 : nonDeletedBackfill.length + 1,
			expanded:   (transformedSlides.length > 0), // Expand if has slides
			children:   childrenWithBackfill,
		};

		if (newProvider.category === 'infotainment' && dynamicDurations && dynamicDurations[newProvider.type]) {
			newProvider.duration = dynamicDurations[newProvider.type];
		} else if (newProvider.category === 'advertising') {
			newProvider.duration = 15;
		}

		console.log('Created new provider:', newProvider);
		return newProvider;
	}, [ transformSlide, dynamicDurations, ]);

	// Save function - updated to work with providers
	// resolution is only sent when the user toggled it (undefined = leave untouched)
	const saveProviders = useCallback(async (campaigns, backfillContent, resolution) => {
		try {
			// Prepare campaigns with their own independent ordering (1, 2, 3...)
			const campaignsData = campaigns
				.filter((provider) => {
					// Unpublished-feed tombstones are dropped permanently on the
					// next save (product decision on DEV-11955): re-publishing
					// mints a new feed id, so the old reference can never revive.
					return !provider.isDeleted && !provider.unavailable;
				})
				.map((provider, index) => {
					return {
						id:          provider.id,
						type:        provider.type,
						// Only save category for dynamic providers, not slide decks / published feeds
						category:    !isDeckLikeType(provider.type) ? (provider.category || null) : null,
						slots:       provider.slots,
						is_backfill: false, // Campaigns are always non-backfill
						order:       index + 1, // Independent campaign ordering: 1, 2, 3...
						settings:    provider.settings || {},
						// Don't save name or slides - they will be fetched dynamically when loading
					};
				});

			// Prepare backfill content with their own independent ordering (1, 2, 3...)
			const backfillData = backfillContent
				.filter((provider) => {
					// Drop unpublished-feed tombstones on save (see campaigns filter)
					return !provider.isDeleted && !provider.unavailable;
				})
				.map((provider, index) => {
					return {
						id:          provider.id,
						type:        provider.type,
						// Only save category for dynamic providers, not slide decks / published feeds
						category:    !isDeckLikeType(provider.type) ? (provider.category || null) : null,
						slots:       provider.slots,
						is_backfill: true, // Backfill content is always backfill
						order:       index + 1, // Independent backfill ordering: 1, 2, 3...
						settings:    provider.settings || {},
						// Don't save name or slides - they will be fetched dynamically when loading
					};
				});

			// Merge them: campaigns first, then backfill (backend will sort by is_backfill then order)
			const providersData = [ ...campaignsData, ...backfillData, ];

			// Update the visual content loop record with the providers JSON
			const response = await API.updateRecord(
				'visual_content_loop',
				visualContentLoopId,
				{
					providers: providersData,
					...(resolution !== undefined && {
						resolution,
					}),
				},
				abortControllerRef.current.signal
			);

			// A save does not itself change deck contents — it only rewrites the loop's
			// provider list — but it is a natural checkpoint: the user has finished a round
			// of edits, so drop the cache and let the next Loop Date change re-read the decks
			// in case any were edited elsewhere while this panel stayed open.
			clearSlideDeckCache();

			return response;
		} catch (error) {
			// If request was aborted, don't show error
			if (error.name === 'AbortError') {
				return;
			}

			// Handle other errors - could show user notification here
			throw error;
		}
	}, [ visualContentLoopId, clearSlideDeckCache, ]);

	// Function to fetch available slide decks from API
	const fetchAvailableSlideDecks = useCallback(async (campaigns, backfillContent) => {
		try {
			const response = await API.findRecords('slide_deck', {
				includes: [
					'slides.slide',
					'slides.galleryFile',
					'slides.folder.files',
				],
			}, abortControllerRef.current.signal);

			if (response && Array.isArray(response)) {
				// Transform API data and filter out slide decks that are already providers
				const existingProviderIds = [ ...campaigns, ...backfillContent, ]
					.filter((provider) => {
						return provider.type === 'slide_deck';
					})
					.map((provider) => {
						return provider.id;
					});

				const availableDecks = response
					.filter((deck) => {
						// Only include slide decks that are not already in providers
						return !existingProviderIds.includes(deck.uuid || deck.id);
					})
					.map((deck) => {
						// Sort slides before transformation
						const sortedSlides = (deck.slides || []).sort((a, b) => {
							return (a.order || 0) - (b.order || 0);
						});

						// Transform the nested slides structure from API
						const transformedSlides = transformApiSlides(sortedSlides, true);
						return {
							id:          deck.id,
							name:        deck.name || 'Untitled Deck',
							type:        'slide_deck',
							slides:      transformedSlides,
							slide_count: transformedSlides.length,
						};
					})
					.sort((a, b) => {
						return a.name.localeCompare(b.name);
					});

				return availableDecks;
			}
			return [];
		} catch (error) {
			// If request was aborted, don't update state
			if (error.name === 'AbortError') {
				return [];
			}

			// For other errors, set empty array
			return [];
		}
	}, [ transformApiSlides, ]);

	// Function to fetch published feeds available to this account (DEV-11955)
	const fetchAvailablePublishedFeeds = useCallback(async (campaigns, backfillContent) => {
		if (!accountUUID) {
			return [];
		}

		try {
			const response = await API.getAvailablePublishedFeeds(accountUUID, abortControllerRef.current.signal);

			if (response && Array.isArray(response)) {
				// Filter out feeds already used as providers, and feeds whose source
				// deck is already addable directly on this account (source account
				// dedupe — the deck shows under Available Slide Decks there).
				const existingFeedIds = [ ...campaigns, ...backfillContent, ]
					.filter((provider) => {
						return provider.type === 'published_feed';
					})
					.map((provider) => {
						return provider.id;
					});

				return response
					.filter((feed) => {
						return !existingFeedIds.includes(feed.published_feed_id);
					})
					.filter((feed) => {
						return feed.source_account_uuid !== accountUUID;
					})
					.map((feed) => {
						return {
							id:                  feed.published_feed_id,
							slide_deck_id:       feed.slide_deck_id,
							name:                feed.name || 'Untitled Feed',
							type:                'published_feed',
							source_account_name: feed.source_account_name,
						};
					})
					.sort((a, b) => {
						return a.name.localeCompare(b.name);
					});
			}
			return [];
		} catch (error) {
			if (error.name === 'AbortError') {
				return [];
			}
			return [];
		}
	}, [ accountUUID, ]);

	return {
		fetchFreshVCLData,
		getInitialProviders,
		addProviderToVCL,
		saveProviders,
		fetchAvailableSlideDecks,
		fetchAvailablePublishedFeeds,
		getProviderType,
		getProviderDisplayName,
	};
};